import React from 'react';
import {
  Component,
  FilterCombo,
  HBox,
  PuzzleCheckbox,
  PuzzleInput,
  VBox,
  noDataDropDownRender,
  PuzzleButton,
  PuzzleDropDownButton,
  Container,
  Position,
  PuzzleToolbar
} from "puzzle"
import {
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuProps,
  GridColumnResizeEvent,
  GridPageChangeEvent,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import { defaultCellRender, getTableTdAccessibilityProps } from "../../../util/Util";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import TextComponent from "puzzle/components/UI/text_component/TextComponent";
import classNames from "classnames";
import ColumnMenu from "puzzle/components/grid/columnmenu/ColumnMenu";
import HeaderCell from "puzzle/components/grid/header_cell_renderer/HeaderCell";
import {
  UPLOAD_REQUEST_PARAMS
} from "../../../Drawer/Tabs/Types";
import {
  COLUMN,
  COMPLETE_REOPEN_RESPONSE,
  DATA,
  FETCHED_DATA,
  FILTER,
  PROPS,
  REOPEN_WORKFLOWS,
  STATE,
  STATIC_GRID_COLUMN,
  TD_PROPS,
  WORKFLOW,
  WS_DELIVER_RESPONSE
} from "./Types"
import * as _ from "lodash";
import { GridColumnProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridColumnProps";
import { debounce } from "lodash";
import { InputChangeEvent } from "@progress/kendo-react-inputs";
import Policies from "../../../util/Policies";
import ActionExecutor from "../../../SupplierView/TaskList/ActionExecutor/ActionExecutor";
import { FILE } from "../UploadDialog/ReportDialog/Types";
import uuid from "uuid-random";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { REPORT_DATA } from "../UploadDialog/Types";
import ReactDOMServer from "react-dom/server";
import { HEADER_CELL_PROPS } from "puzzle/components/grid/header_cell_renderer/Types";

class ReopenDialog extends Component<PROPS, STATE> {
  gridConfigTemp: COLUMN[] = [];
  initData: FETCHED_DATA[] = [];
  initWorkflowData: REOPEN_WORKFLOWS[] = [];
  showDeliverViaWS: boolean = false;
  showOpenButton: boolean = false;

  gridConfig: STATIC_GRID_COLUMN[] = [{
    dataIndex: 'selected',
    width: 50,
    filterable: false,
    sortable: false
  }, {
    dataIndex: 'fileName',
    width: 400,
    filterable: false,
    sortable: true
  }, {
    dataIndex: 'targetLanguageDisplayName',
    width: 220,
    filterable: true,
    sortable: true,
    type: 'list-search',
    filterValues: []
  }, {
    dataIndex: 'batchName',
    width: 200,
    filterable: true,
    sortable: true,
    type: 'list',
    filterValues: []
  }, {
    dataIndex: 'status',
    width: 200,
    filterable: true,
    sortable: false,
    type: 'list',
    filterValues: [
      "COMPLETED",
      "IN_ERROR",
      "NOT_MODIFIED",
      "UPLOADED"
    ]
  }, {
    dataIndex: 'lastChangeDate',
    width: 200,
    filterable: false,
    sortable: false
  }];

  static defaultProps: PROPS = {
    pageSize: 20,
    rowHeight: 40,
    massSelectionCount: 200
  };

  public state: STATE = {
    columns: [],
    data: [],
    parsableExist: false,
    filterMenuOpen: '',
    filters: {},
    selectAll: false,
    skip: 0,
    massSelection: false,
    sort: [{
      dir: 'asc',
      field: 'fileName'
    }],
    total: 0,
    workFlows: [],
    wfValue: null,
    reopenInProcess: !!this.props.reopenInProcess
  };

  constructor(props: PROPS) {
    super(props);
    this.showDeliverViaWS = Policies.hasDeliverViaWS;
    this.showOpenButton = Policies.hasUploadTranslatable;
  }

  /**
   * Grid cell render
   * @param cellProps
   */
  cellRender = (cellProps: GridCellProps): JSX.Element => {
    const {
      dataItem,
      field
    } = cellProps;
    const { reopenInProcess } = this.state;
    const tdProps: TD_PROPS = getTableTdAccessibilityProps(cellProps);
    const disabledClass = reopenInProcess ? 'k-disabled' : '';

    let child;
    switch (field) {
      case 'selected':
        tdProps.className = 'tp-select-cell';
        child = this.selectCellRenderer(cellProps);
        break;
      case 'fileName':
        tdProps.className = `tp-file-name-cell ${disabledClass}`;
        child = this.fileNameRenderer(cellProps);
        break;
      case 'lastChangeDate':
        let dueDate = dataItem.lastChangeDate;
        dueDate = dueDate ? new Date(dueDate) : '';
        child = this.dueDateRenderer(dueDate);
        break;
      case 'status':
        child = this.statusRenderer(cellProps);
        break;
      default:
        child = defaultCellRender(this.translate, this.loadingCell, cellProps);
        break;
    }
    return <td {...tdProps} className={'tp-sortable'}>{child}</td>
  };

  /**
   * Check if item fulfills all filter conditions
   *
   * @param item
   * @param newFilterObject
   */
  checkAllFilterConditions = (item: FETCHED_DATA, newFilterObject: FILTER): boolean => {
    let keys = Object.keys(newFilterObject);
    let condition = true;
    let i = 0;

    while (keys.length > i && condition) {
      condition = condition && this.checkSingleCondition(keys[i], item, newFilterObject)
      i++;
    }
    return condition;
  };

  /**
   * Check if there are any validation errors with in selection
   * @param selectAll
   */
  checkForIrregularities = (selectAll: boolean) => {
    const {
      data,
      wfValue
    } = this.state;
    let powerWorkflow = false;  //Power user workflow type does not require file upload
    let validationIrregularity: string = '';
    let linkedWfBatches: number[] = [];

    if (wfValue) {
      powerWorkflow = wfValue.linkedWorkflowType === 'POWER_USER';
      linkedWfBatches = this.getAllBatchesContainingLinkedWorkflowId(wfValue.linkedWorkflowId);
    } else {
      validationIrregularity = 'noWorkflowSelected';
    }

    let i = 0;
    let batches: number[] = [];
    let collectedTargets: number[] = [];
    while (i < data.length && !validationIrregularity) {
      if (selectAll || data[i].selected) {
        batches = _.union(batches, [data[i].batchId])
        if (batches.length > 1) {
          validationIrregularity = 'multiBatch';
          collectedTargets = [];
          break;
        }

        if (!powerWorkflow && data[i].status !== 'UPLOADED') {
          validationIrregularity = 'notUploadedTarget';
          collectedTargets = [];
          break;
        }

        if (!_.includes(linkedWfBatches, data[i].batchId)) {
          validationIrregularity = 'wrongLinkedWorkflow';
          collectedTargets = [];
          break;
        }

        collectedTargets.push(data[i].targetId);
      }
      i++;
    }

    return {
      validationIrregularity,
      collectedTargets
    };
  };

  /**
   * Check if there is at least one UPLOADED target in dialog
   */
  checkForUploadedFiles = (): boolean => {
    return this.state.data.some((item) => item.status === 'UPLOADED');
  };

  /**
   * Check if item fulfills filter criteria
   *
   * @param name
   * @param item
   * @param newFilterObject
   */
  checkSingleCondition = (name: string, item: any, newFilterObject: FILTER) => {
    return name === 'fileName' ? item[name].toLowerCase().includes((newFilterObject[name] as string).toLowerCase()) : _.includes(newFilterObject[name], item[name]);
  };

  /**
   * clearAllHandler clears all filters and resets sorts
   */
  clearAllHandler = (): void => {
    const workFlows = this.filterWorkflowsByBatchIds(undefined);

    this.setState({
      data: _.cloneDeep(this.initData).map((item: FETCHED_DATA) => {
        return {
          ...item,
          selected: false
        }
      }),
      filterMenuOpen: '',
      filters: {},
      massSelection: this.initData.length >= this.props.massSelectionCount,
      sort: [{
        dir: 'asc',
        field: 'fileName'
      }],
      selectAll: false,
      skip: 0,
      total: this.initData.length,
      workFlows,
      wfValue: null
    });
  };

  /**
   * Complete action handler
   * @param completeAction
   */
  completeHandler = (completeAction: DropDownButtonItemClickEvent) => {
    const {
      all = false
    } = completeAction.item
    let {
      validationIrregularity,
      collectedTargets
    } = this.checkForIrregularities(all);

    if (validationIrregularity) {
      this.throwValidationWarning(validationIrregularity);
    } else {
      this.dispatch('reopenCompleteAction', {
        dispatcherId: 'reopenDispatcher',
        pos: Position.end,
      }, {
        submissionId: this.props.submissionData.submissionId,
        reopenInProcess: true,
        notificationId: uuid(),
        completeParams: {
          reopenWorkflowId: this.state.wfValue?.linkedWorkflowId,
          searchRequest: {
            targetIds: collectedTargets
          }
        }
      })
    }
  };

  /**
   * Creates groups for dropDownButtons
   * @param group
   */
  createDropDownItems = (group: string) => {
    const selected = this.getSelectedCount();
    const { total } = this.state;

    if (group === 'download') {
      return [{
        disabled: !selected,
        group,
        text: this.translate('Download.selectedFinalTxlf', { files: selected }),
        type: "finalTxlf"
      }, {
        all: true,
        group,
        text: this.translate('Download.allFinalTxlf', { files: total }),
        type: "finalTxlf"
      }, {
        disabled: !selected,
        group,
        text: this.translate('Download.selectedNativeFiles', { files: selected }),
        type: "nativeFiles"
      }, {
        all: true,
        group,
        text: this.translate('Download.allNativeFiles', { files: total }),
        type: "nativeFiles"
      }, {
        disabled: !selected,
        group,
        text: this.translate('Download.selectedAuxiliaryFiles', { files: selected }),
        type: "auxiliaryFiles"
      }, {
        all: true,
        group,
        text: this.translate('Download.allAuxiliaryFiles', { files: total }),
        type: "auxiliaryFiles"
      }]
    }
    return [{
      disabled: !selected,
      group,
      text: this.translate('selectedFiles', { files: selected })
    }, {
      all: true,
      group,
      text: this.translate('allFiles', { files: total })
    }];
  };

  /**
   * Performs data filter with keydown delay
   */
  delayedSearch = () => {
    let {
      data,
      parsableExist,
      workFlows
    } = this.rearrangeData(this.state.filters);

    this.setState({
      data: data,
      massSelection: data.length >= this.props.massSelectionCount,
      parsableExist,
      selectAll: false,
      skip: 0,
      total: data.length,
      wfValue: null,
      workFlows
    });
  };


  /**
   * Deliver via Webservices handler.
   * @param deliverAction
   */
  deliverViaWsHandler = (deliverAction: DropDownButtonItemClickEvent) => {
    const {
      all = false,
    } = deliverAction.item;
    const { data } = this.state;

    let i = 0;
    let batches: number[] = [];
    let validationIrregularity = '';
    let collectedTargets = [];
    while (i < data.length) {
      if (all || data[i].selected) {
        batches = _.union(batches, [data[i].batchId])
        if (batches.length > 1) {
          validationIrregularity = 'deliverMultiBatch';
          collectedTargets = [];
          break;
        }
        collectedTargets.push(data[i].targetId);
      }
      i++;
    }

    if (!validationIrregularity) {
      this.dispatch('reopenDeliver', {
        dispatcherId: 'reopenDispatcher',
        pos: Position.end
      }, {
        submissionId: this.props.submissionData.submissionId,
        reopenInProcess: true,
        notificationId: uuid(),
        params: {
          targetIds: collectedTargets
        }
      })
    } else {
      this.throwValidationWarning(validationIrregularity);
    }
  };

  /**
   * Due date cell render
   */
  dueDateRenderer = (date: Date | string): JSX.Element => {
    return <> {this.translateByNamespace('date.dateWithTime', { date })} </>;
  };

  /**
   * File name cell render
   * @param cellProps
   */
  fileNameRenderer = (cellProps: GridCellProps) => {
    const { reopenInProcess } = this.state;
    const { dataItem, field } = cellProps;
    let value = '';
    if (field) {
      value = dataItem[field];
    }

    const fileNameCell = !dataItem.nonParsable && this.showOpenButton ?
      (
        <span
          role='link'
          className='tp-link tp-text-underline'
          onClick={() => !reopenInProcess && this.onFileClick(dataItem)}
          tabIndex={0}
        >
          {value}
        </span>
      ) : (
        value
      );

    return fileNameCell;
  };

  /**
   * Gets all workflows conected to selected batchId's
   * @param batchIds
   */
  filterWorkflowsByBatchIds = (batchIds?: number[]) => {
    const wfs = this.initWorkflowData.reduce((acc: WORKFLOW[], curr: REOPEN_WORKFLOWS) => {
      if (batchIds) {
        if (_.includes(batchIds, curr.batchId)) {
          acc = _.unionBy(acc, curr.reopenSubmissionWorkflows, 'linkedWorkflowId');
        }
      } else {

        acc = _.unionBy(acc, curr.reopenSubmissionWorkflows, 'linkedWorkflowId');
      }
      return acc;
    }, []);

    return wfs;
  };

  /**
   * Get all batches that are connected to selected linked workflow
   */
  getAllBatchesContainingLinkedWorkflowId = (id: number) => {
    let batchIds: number[] = this.initWorkflowData.reduce((acc: number[], item: REOPEN_WORKFLOWS) => {
      let batchHasWf = !!_.find(item.reopenSubmissionWorkflows, { 'linkedWorkflowId': id });
      if (batchHasWf) {
        acc = acc.concat(item.batchId);
      }
      return acc;
    }, []);

    return batchIds;
  };

  /**
   * Method that sets class for opened or filter active class for header
   */
  getHeaderClass = (dataIndex: string) => {
    const {
      filterMenuOpen,
      filters
    } = this.state;
    const gridColumn = this.gridConfig.find(column => column.dataIndex === dataIndex);

    const headerClasses = classNames({
      'tp-filter-applied': !!filters[dataIndex],
      'tp-filter-opened': dataIndex === filterMenuOpen,
      'tp-grid-header-with-filter': gridColumn?.filterable
    });

    return headerClasses;
  };

  /**
   * Returns number of selected record's
   */
  getSelectedCount = () => {
    const { data } = this.state;
    const selectedData = data.filter((item: FETCHED_DATA) => item.selected);
    return selectedData.length;
  };

  /**
   * Upload params getter
   */
  getUploadParams = (): UPLOAD_REQUEST_PARAMS => {
    const {
      submissionId
    } = this.props.submissionData;
    return {
      downloadReportUrl: '/submissions/download/upload/report',
      uploadDialogUrl: `/submissions/${submissionId}/reopen/upload`,
      uploadReportGetUrl: `/submissions/${submissionId}/upload/report/`
    }
  };

  /**
   * Header selection handler
   */
  headerSelectionChange = () => {
    let { data, selectAll } = this.state;
    selectAll = !selectAll;

    let newData = data.map((item: FETCHED_DATA) => {
      return {
        ...item,
        selected: selectAll
      }
    });

    this.setState({
      data: newData,
      selectAll
    });
  };

  /**
   * Handler for dropdown items click.
   * @param element
   */
  itemClick = (element: DropDownButtonItemClickEvent): void => {
    let {
      group,
    } = element.item;

    switch (group) {
      case "download":
        this.downloadReopen(element)
        break;
      case "complete":
        this.completeHandler(element);
        break;
      case "deliverViaWs":
        this.deliverViaWsHandler(element);
        break;
      case "open":
        this.openHandler(element);
        break;
      default:
        console.error('Invalid group name');
        break;
    }
  };


  /**
   * Return loading cell if data is not populated
   * @param cellProps
   */
  loadingCell = (cellProps: GridCellProps) => {
    let result = null;
    if (cellProps.dataItem.targetId === undefined) {
      result = <span className="k-icon k-i-loading" />;
    }

    return result;
  };

  /**
   * Method for updating filter selection
   *
   * @param  property
   * @param value
   */
  onChangeFilters = (property: string, value: string[]) => {
    const {
      filters
    } = this.state;
    let newFilterObject: FILTER;

    if (value.length) {
      newFilterObject = _.cloneDeep(filters);
      newFilterObject[property] = value;
    } else {
      newFilterObject = _.omit(filters, property);
    }

    let {
      data,
      parsableExist,
      workFlows
    } = this.rearrangeData(newFilterObject);

    this.setState({
      data,
      filters: {
        ...newFilterObject
      },
      massSelection: data.length >= this.props.massSelectionCount,
      parsableExist,
      selectAll: false,
      skip: 0,
      total: data.length,
      wfValue: null,
      workFlows
    });
  };

  /**
   * Set new column width on resize
   * @param e
   */
  onColumnResize = (e: GridColumnResizeEvent) => {
    //@ts-ignore
    const column = e.target._columns[e.index];
    const index = _.findIndex(this.gridConfigTemp, { dataIndex: column.field });
    this.gridConfigTemp[index].width = e.newWidth;

    debounce(() => {
      this.setState({ columns: this.gridConfigTemp });
    }, 500);
  };

  /**
   * File click handler
   * @param dataItem
   */
  onFileClick = (dataItem: FETCHED_DATA) => {
    ActionExecutor.setPingingInstance(this.moduleName);
    this.dispatch('getOnlineEditorForReopen', {
      dispatcherId: 'reopenTsoPing'
    }, {
      submissionId: this.props.submissionData.submissionId,
      onlineEditorParams: {
        editor: 'TSO',
        entityIds: [dataItem.targetId]
      }
    });
  };

  /**
   * Delayed search
   */
  onInputChangeDelay = debounce(this.delayedSearch, 500);

  /**
   * Checks if column can be sorted and handles sorting
   * @param e : GridSortChangeEvent
   */
  onSortChange = (e: GridSortChangeEvent) => {
    const { sort } = e;
    const { data } = this.state;

    let sortedData = _.orderBy(data, [sort[0].field], [sort[0].dir as "asc" | "desc"]);
    this.setState({
      data: sortedData,
      sort
    });
  };

  /**
   * Open button handler
   * @param element
   */
  openHandler = (element: DropDownButtonItemClickEvent) => {
    let {
      all = false,
    } = element.item;

    ActionExecutor.setPingingInstance(this.moduleName);
    this.dispatch('getOnlineEditorForReopen', {
      dispatcherId: 'reopenTsoPing'
    }, {
      submissionId: this.props.submissionData.submissionId,
      onlineEditorParams: {
        editor: 'TSO',
        entityIds: this.state.data.reduce((acc: number[], item: FETCHED_DATA) => {
          if (all || item.selected) {
            acc = acc.concat(item.targetId);
          }
          return acc;
        }, [])
      }
    });
  };

  /**
   * Page event handler, set skip parameter
   */
  pageChange = (event: GridPageChangeEvent) => {
    const skip = event.page.skip;
    this.setState({
      skip
    });
  };

  /**
   * Grid header cell render
   * @param thProps
   */
  puzzleHeaderRender = (thProps: HEADER_CELL_PROPS): JSX.Element => {
    return <HeaderCell
      {...thProps}
    />;
  };

  /**
   * Localizes data for status filter
   *
   * @param column
   * @param data
   */
  prepareFilterData = (column: string, data: string[]) => {
    return data.map((item: string) => {
      return {
        name: column === 'status' ? this.translate(`status.${item}`) : item,
        value: item
      }
    });
  };

  /**
   * Returns all data that fulfills all filter conditions and conditionally sorts data
   * @param filters
   */
  rearrangeData = (filters: FILTER) => {
    const { sort } = this.state;
    let parsableExist = false;
    let cond: boolean;
    let uniqueBatchIds: number[] = [];

    let data = this.initData.filter((item: FETCHED_DATA) => {
      cond = this.checkAllFilterConditions(item, filters);
      if (this.showOpenButton && cond && !parsableExist) {
        parsableExist = !item.nonParsable;
      }
      if (cond) {
        uniqueBatchIds = _.union(uniqueBatchIds, [item.batchId]);
      }

      return cond;
    });

    if (sort[0].field !== 'fileName' && sort[0].dir !== 'asc') {
      data = _.orderBy(data, [sort[0].field], [sort[0].dir as "asc" | "desc"]);
    }

    let workFlows = this.filterWorkflowsByBatchIds(uniqueBatchIds);

    return {
      data,
      parsableExist,
      workFlows
    };
  };

  /**
   * method that renders files grid columns
   * @param column
   */
  renderColumn = (column: STATIC_GRID_COLUMN) => {
    let dataIndex: string = column.dataIndex;
    let width = _.find(this.state.columns, { 'dataIndex': dataIndex })?.width || column.width;

    let columnProps: GridColumnProps = {
      field: dataIndex,
      minResizableWidth: 120,
      // @ts-ignore
      headerCell: dataIndex === 'selected' ? this.selectedHeaderColumnCellRender : this.puzzleHeaderRender,
      title: this.translate(`columnHeaders.${dataIndex}`),
      cell: this.cellRender,
      columnMenu: (column.sortable || column.filterable) ? this.renderColumnMenuCheckboxFilter : undefined,
      resizable: dataIndex !== 'selected',
      width: width,
      headerClassName: classNames(this.getHeaderClass(dataIndex), {
        'tp-sortable': column.sortable
      })
    };

    return <Column
      key={dataIndex}
      {...columnProps}
    />
  };


  /**
   * Method for rendering filter menus
   * @param props
   */
  renderColumnMenuCheckboxFilter = (props: GridColumnMenuProps): JSX.Element | null => {
    const {
      filters
    } = this.state;
    const columnName: string = props.column.field as string;
    let column = _.find(this.gridConfig, { 'dataIndex': columnName });

    let columnMenuProps: any = {
      closeMenu: props.onCloseMenu,
      type: 'input',
      filterable: false,
      sortable: false
    };

    //add filter props
    if (column?.filterable) {
      columnMenuProps = {
        ...columnMenuProps,
        data: column.filterValues,
        type: column.type,
        filterable: true,
        onFilterChange: this.onChangeFilters
      }

      if (column.filterValues) {
        let data = this.prepareFilterData(columnName, column.filterValues)
        columnMenuProps = {
          ...columnMenuProps,
          data,
          values: filters[columnName] || []
        }
      }
    }

    // add sort props
    if (column?.sortable) {
      columnMenuProps = {
        ...columnMenuProps,
        sortable: true,
        onSortChange: this.onSortChange
      }
    }

    return <ColumnMenu
      setActiveHeader={this.setFilterMenuOpen}
      column={{ field: columnName }}
      {...columnMenuProps}
    />
  };

  /**
   * UpperSectionRender for ReopenGrid
   * scope of function is changed in <Files/>
   */
  renderUpperSection() {
    const { fileName } = this.state.filters;
    let searchValue = fileName || '';

    return (
      <>
        <Container
          className={'tp-reopen-topbar'}
          alignItemsCenter
          gap={2}
          hBox
          justifyContentBetween
          wrap
        >
          <HBox
            className={'tp-search-reopen-wrap'}
            flex1
            gap={2}>
            <PuzzleInput
              searchField={true}
              onChange={this.searchBarChange}
              placeholder={this.translate('search')}
              value={searchValue}
            />
            <PuzzleButton
              themeColor={'primary'}
              disabled={!this.getSelectedCount() && !Object.keys(this.state.filters).length}
              fillMode={'outline'}
              onClick={this.clearAllHandler}
            >
              {this.translate('clearAll')}
            </PuzzleButton>
          </HBox>
          {this.renderUpperToolbar()}
        </Container>
      </>
    )
  };

  /**
   * Downloads selected files
   * @param event
   */
  downloadReopen = (event: DropDownButtonItemClickEvent) => {
    const { data } = this.state
    const { item } = event;
    const { all } = item
    const selectedFiles = data.filter((item: FETCHED_DATA) => item.selected);
    const files = all ? data : selectedFiles
    let params: {
      downloadType: string,
      targetIds: number[]
    } = {
      downloadType: item.type,
      targetIds: files.map((file: FETCHED_DATA) => file.targetId)
    };

    this.dispatch('pingUntilDownloadIsReady', '', {
      params,
      errorHandlerPrefix: 'REOPEN_',
      additionalData: {
        afterDownloadOperation: 'resetReopenData'
      }
    });
  }


  /**
   * Render for upper section toolbar
   */
  renderLowerToolbar = () => {
    const { reopenInProcess } = this.state;
    return (
      <PuzzleToolbar>
        <Container flex1 />
        <PuzzleButton
          fillMode={'flat'}
          onClick={() => {
            this.dispatch('exitReopen')
          }}
        >
          {this.translate('close')}
        </PuzzleButton>
        {
          this.showDeliverViaWS && (
            <PuzzleDropDownButton
              disabled={reopenInProcess}
              aria-label={this.translate('wsDeliver')}
              icon={'arrow-s'}
              items={this.createDropDownItems('deliverViaWs')}
              onItemClick={this.itemClick}
              reverse
              text={this.translate('wsDeliver')}
              themeColor={'primary'}
            />
          )
        }
        <PuzzleDropDownButton
          disabled={reopenInProcess}
          aria-label={this.translate('complete')}
          icon={'arrow-s'}
          items={this.createDropDownItems('complete')}
          onItemClick={this.itemClick}
          reverse
          text={this.translate('complete')}
          themeColor={'primary'}
        />
      </PuzzleToolbar>
    )
  };

  /**
   * Render for lower section toolbar
   */
  renderUpperToolbar = (): JSX.Element => {
    const { reopenInProcess } = this.state;
    return (
      <PuzzleToolbar className={'tp-topbar-actions'}>
        {
          this.showOpenButton && this.state.parsableExist && (
            <PuzzleDropDownButton
              disabled={reopenInProcess}
              aria-label={this.translate('open')}
              icon={'arrow-s'}
              items={this.createDropDownItems('open')}
              onItemClick={this.itemClick}
              reverse
              text={this.translate('open')}
              themeColor={'primary'}
            />
          )
        }
        <PuzzleDropDownButton
          disabled={reopenInProcess}
          aria-label={this.translate('download')}
          popupSettings={{ popupClass: 'pd-reopen-download-dropdown' }}
          icon={'arrow-s'}
          items={this.createDropDownItems('download')}
          onItemClick={this.itemClick}
          reverse
          text={this.translate('download')}
          themeColor={'primary'}
        />
        <PuzzleButton
          disabled={reopenInProcess}
          themeColor={'primary'}
          onClick={() => this.uploadFiles()}
        >
          {this.translate('upload')}
        </PuzzleButton>
      </PuzzleToolbar>
    )
  };

  /**
   * Refreshes grid with new data and resets processing flag after DeliverViaWS action
   */
  resetProcessingAfterDeliverViaWS = (reopenRequestData: WS_DELIVER_RESPONSE) => {
    const {
      deliveredViaWebServices = []
    } = reopenRequestData;

    let filteredData: FETCHED_DATA[] = [];

    this.initData = _.cloneDeep(this.initData).map((item: FETCHED_DATA) => {
      let data: { status: string, lastChangeDate: number } | {} = {};

      if (_.includes(deliveredViaWebServices, item.targetId)) {
        data = {
          status: 'COMPLETED',
          lastChangeDate: new Date().getTime()
        };
      } else if (item.previousStatus) {
        data = {
          status: item.previousStatus,
          previousStatus: undefined
        }
      }
      if (this.checkAllFilterConditions(item, this.state.filters)) {
        filteredData.push({ ...item, ...data });
      }
      return {
        ...item,
        ...data
      }
    });

    this.setState({
      data: filteredData,
      reopenInProcess: false,
      selectAll: false
    });
  };

  /**
   * Refreshes grid with new data and resets processing flag after Complete action
   */
  resetProcessing = (reopenRequestData: COMPLETE_REOPEN_RESPONSE) => {
    const {
      reopenTargets = [],
      failedTargets = []
    } = reopenRequestData;

    let filteredData: FETCHED_DATA[] = [];

    this.initData = _.cloneDeep(this.initData).map((item: FETCHED_DATA) => {
      let data: { status: string, lastChangeDate: number } | {} = {};

      if (_.includes(reopenTargets, item.targetId)) {
        data = {
          status: 'COMPLETED',
          lastChangeDate: new Date().getTime()
        };
      } else if (_.includes(failedTargets, item.targetId)) {
        data = {
          status: 'IN_ERROR',
          lastChangeDate: new Date().getTime()
        }
      }
      if (this.checkAllFilterConditions(item, this.state.filters)) {
        filteredData.push({ ...item, ...data });
      }

      return {
        ...item,
        ...data,
        previousStatus: undefined
      }
    });

    this.setState({
      data: filteredData,
      reopenInProcess: false,
      selectAll: false
    });
  };

  /**
   * Search bar input handler
   * @param e
   */
  searchBarChange = (e: InputChangeEvent): void => {
    const { value } = e;
    const { filters } = this.state;
    let newFilters: FILTER = {
      ...filters,
      fileName: value
    };

    newFilters = value ? newFilters : _.omit(newFilters, ['fileName']);
    this.setState({
      filters: newFilters
    }, this.onInputChangeDelay)
  };

  /**
   * Selection cell render
   * @param cellProps
   */
  selectCellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem } = cellProps;
    const { reopenInProcess } = this.state;
    return (
      <PuzzleCheckbox
        checked={dataItem['selected']}
        disabled={reopenInProcess}
        onChange={this.selectionChange.bind(this, dataItem)}
        aria-label={this.translate("SRL-checkboxHeaderLabel")}
      />
    )
  };

  /**
   * Grid Selection Change handler
   * @param dataItem
   */
  selectionChange = (dataItem: FETCHED_DATA): void => {
    const {
      data,
      massSelection,
      selectAll,
    } = this.state;
    const { massSelectionCount } = this.props;

    let selectedTargets = this.getSelectedCount();
    selectedTargets = selectedTargets + (dataItem.selected ? -1 : 1);
    const allowSelection = !(massSelection && selectAll) && (selectedTargets <= massSelectionCount);

    if (allowSelection) {
      let changedData = _.cloneDeep(data);
      let arrayIndex = changedData.findIndex((currentValue: FETCHED_DATA) => currentValue.targetId === dataItem.targetId);
      changedData[arrayIndex].selected = !changedData[arrayIndex].selected;
      const selectAll = changedData.every((item: FETCHED_DATA) => item.selected);

      this.setState({
        data: changedData,
        selectAll
      });
    } else {
      this.dispatch('maxFileSelectionExceededWarning', '', {
        message: this.translate('maxFileSelectionExceeded', { limit: massSelectionCount })
      })
    }
  };

  /**
   * Sets Source files grid data.
   * @param data
   * @param workflows
   */
  setGridData = (data: DATA[], workflows: REOPEN_WORKFLOWS[]): void => {
    let batchNameValues: string[] = [];
    let targetLanguageDisplayName: string[] = [];
    let parsableExist = false;
    let hasProcessing: boolean = false;

    //data setup
    this.initData = _.orderBy(data, ['fileName'], ['asc']).map((i: DATA) => {
      batchNameValues = _.union(batchNameValues, [i.batchName]);
      targetLanguageDisplayName = _.union(targetLanguageDisplayName, [i.targetLanguageDisplayName]);
      if (this.showOpenButton && !parsableExist) {
        parsableExist = !i.nonParsable;
      }

      if (!hasProcessing && i.status === 'PROCESSING') {
        hasProcessing = true;
      }

      return {
        ...i,
        selected: false,
        previousStatus: undefined
      }
    });

    //filter setup
    let batchName = _.find(this.gridConfig, { 'dataIndex': 'batchName' });
    if (batchName) {
      batchName.filterValues = batchNameValues;
    }

    let targetLanguage = _.find(this.gridConfig, { 'dataIndex': 'targetLanguageDisplayName' });
    if (targetLanguage) {
      targetLanguage.filterValues = targetLanguageDisplayName;
    }

    //wfs setup
    this.initWorkflowData = workflows;
    const wfs: WORKFLOW[] = this.filterWorkflowsByBatchIds(undefined);

    // temp grid setup(resize)
    this.gridConfigTemp = this.gridConfig.map((item) => {
      return _.pick(item, ['dataIndex', 'width'])
    });

    this.setState({
      data: this.initData,
      total: this.initData.length,
      parsableExist,
      massSelection: this.initData.length >= this.props.massSelectionCount,
      workFlows: wfs,
      wfValue: null,
      columns: this.gridConfigTemp,
      ...(hasProcessing ? { reopenInProcess: hasProcessing } : null)
    });
  };

  /**
   * methods that set currently opened filter menu for given column
   * @param columnName - name of column
   */
  setFilterMenuOpen = (columnName?: string) => {
    this.setState({ filterMenuOpen: columnName || '' })
  };

  /**
   * Custom selected column header cell render
   */
  selectedHeaderColumnCellRender = (): JSX.Element => {
    const { selectAll } = this.state;
    return (
      <PuzzleCheckbox
        checked={selectAll}
        onChange={this.headerSelectionChange}
        aria-label={this.translate("SRL-checkboxHeaderLabel")}
      />
    )
  };

  /**
   * Move targets to processing status
   * @param targets
   * @param disable
   */
  setProcessingTargets = (targets: number[], disable: boolean) => {
    let filteredData: FETCHED_DATA[] = [];
    this.initData = _.cloneDeep(this.initData).map((item: FETCHED_DATA) => {
      let data: { status: string, lastChangeDate: number } | {} = {};
      if (_.includes(targets, item.targetId)) {
        data = {
          status: 'PROCESSING',
          previousStatus: item.status
        }
      }

      if (this.checkAllFilterConditions(item, this.state.filters)) {
        filteredData.push({ ...item, ...data });
      }

      return {
        ...item,
        ...data
      }
    });

    this.setState({
      data: filteredData,
      reopenInProcess: disable,
      selectAll: false
    });
  };


  /**
   * Status column cell render
   * @param cellProps
   */
  statusRenderer = (cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let value = '';
    if (field) {
      value = dataItem[field];
    }
    const cellStatusCls = classNames('tp-reopen-grid-reopenStatus-cell ', value.toLowerCase());

    let statusCell = (
      <>
        <div
          className={`${cellStatusCls}`}
        >
          {this.translate(`status.${value}`)}
        </div>
      </>
    )

    return statusCell
  };

  /**
   * If there were uploads on TSO change status on uploaded targets.
   * @param report
   */
  setUploadedTargets = (report: REPORT_DATA) => {
    let {
      uploadReportsDto
    } = report;
    let uploadedTargets: any[] = [];

    if (uploadReportsDto?.length) {
      uploadedTargets = uploadReportsDto.reduce((acc: number[], item: FILE) => {
        if (item.fileUploadStatus === 'UPLOADED') {
          acc = acc.concat(item.targetId);
        }
        return acc
      }, []);
    }

    let filteredData: FETCHED_DATA[] = [];
    this.initData = _.cloneDeep(this.initData).map((item: FETCHED_DATA) => {
      let data: { status: string, lastChangeDate: number } | {} = {};
      if (_.includes(uploadedTargets, item.targetId)) {
        data = {
          status: 'UPLOADED',
          lastChangeDate: new Date().getTime()
        };
      }
      if (this.checkAllFilterConditions(item, this.state.filters)) {
        filteredData.push({ ...item, ...data });
      }

      return {
        ...item,
        ...data
      }
    });

    this.setState({
      data: filteredData,
      selectAll: false
    });
  };

  /**
   * Show's warning dialog with proper validation erorr message
   * @param validationErrorType
   */
  throwValidationWarning = (validationErrorType: string): void => {
    let text: any = this.translate(`warnings.${validationErrorType}.text`, {
      className: 'tp-text-semibold'
    });
    text = ReactDOMServer.renderToString(text);
    let locales = {
      title: this.translate(`warnings.${validationErrorType}.title`),
      text
    };

    this.dispatch('reopenWarningTemplate', '', { ...locales });
  };

  /**
   * Open upload dialog
   * @param files
   */
  uploadFiles = (files?: FileList) => {
    const {
      submissionId,
      submissionName,
    } = this.props.submissionData;
    const submissionData = {
      submissionId,
      submissionName
    };

    const uploadParams = this.getUploadParams();
    this.dispatch('openUploadDialog', '', {
      reopenDialogFlow: true,
      submissionData,
      ...uploadParams,
      ...(files && { droppedFiles: files })
    });
  };

  /**
   * @inheritDoc
   */
  renderBody = () => {
    let {
      data,
      reopenInProcess,
      skip,
      total,
      sort,
      workFlows,
      wfValue
    } = this.state;

    let {
      pageSize,
      rowHeight
    } = this.props;

    return (
      <>
        {this.renderUpperSection()}
        <VBox className={'tp-reopen-dialog-middle'} gap={4}>
          <KendoGridAbstract
            style={{
              height: "100%"
            }}
            captionText={this.translate('SRL-downloadSourceFilesTableCaption')}
            data={data.slice(skip, skip + pageSize + 5)}
            dragAndDropEnabled
            dNdMessage={this.translate('dragAndDropText')}
            onPageChange={this.pageChange}
            onColumnResize={this.onColumnResize}
            pageSize={pageSize}
            resizable={true}
            rowHeight={rowHeight}
            scrollable={'virtual'}
            selectedField={'selected'}
            skip={skip}
            sort={sort}
            total={total}
            uploadFiles={this.uploadFiles}
          >
            {this.gridConfig.map(this.renderColumn)}
          </KendoGridAbstract>
          <HBox
            alignItemsCenter
            gap={5}
            className={'tp-workflow-field-wrap'}
          >
            <TextComponent
              className={'k-text-nowrap'}
              thickness={'semi-bold'}>
              {this.translate('selectWf')}
            </TextComponent>
            <FilterCombo
              data={workFlows}
              label={this.translate('selectWf')}
              labelClassName={'sr-only'}
              disabled={reopenInProcess}
              value={wfValue}
              required={true}
              textField={'linkedWorkflowName'}
              dataItemKey={'linkedWorkflowId'}
              listNoDataRender={() => noDataDropDownRender(this.translate('emptyWorkflow'))}
              onChange={(event: ComboBoxChangeEvent) => {
                this.setState({
                  wfValue: event.value
                })
              }}
            />
          </HBox>
        </VBox>
        <DialogActionsBar>
          <div className={'tp-dialog-footer-toolbar tp-left-and-right'}>
            <span
              role="alert"
              aria-live="polite"
              className={'tp-selected-files-counter'}
            >
              {this.translate('countTxt', { count: this.getSelectedCount() })}
            </span>
            <Container flex1 />
            {this.renderLowerToolbar()}
          </div>
        </DialogActionsBar>
      </>
    );
  }
}

export default ReopenDialog;
